import type { Ref } from 'vue';
import {
  computed,
  ref,
} from 'vue';

import type { IContextQueryResult } from '@modules/core/services/query/types';
import type {
  IExtendedTableDataResponse,
  ITableState,
  ITableStateUpdateAction,
} from '@modules/tables/types';
import type { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from '@tanstack/vue-query';

export interface TableDataComposable<T, AdditionalData> {
  tableState: Ref<ITableState>;
  filterSetId: Ref<number | undefined>;
  data: Ref<IExtendedTableDataResponse<T, AdditionalData> | undefined>;
  isLoading: Ref<boolean>;
  isFetching: Ref<boolean>;
  updateTableState(state: Partial<ITableState>, action?: ITableStateUpdateAction): void;
  setData(updater: (data: IExtendedTableDataResponse<T, AdditionalData> | undefined) => IExtendedTableDataResponse<T, AdditionalData> | undefined): void;
  refetch(options?: (RefetchOptions & RefetchQueryFilters) | undefined): Promise<QueryObserverResult<IExtendedTableDataResponse<T, AdditionalData>, Error>>;
  refreshTableData(): Promise<QueryObserverResult<IExtendedTableDataResponse<T, AdditionalData>, Error>>;
}

type UseTableQuery<T, AdditionalData, ExtendedData extends unknown[]> = (
  tableState: Ref<ITableState>,
  ...args: ExtendedData
) => IContextQueryResult<IExtendedTableDataResponse<T, AdditionalData>, Error>;

export default function useTableData<T, AdditionalData, ExtendedData extends unknown[]>(
  useQuery: UseTableQuery<T, AdditionalData, ExtendedData>,
  ...args: ExtendedData
): TableDataComposable<T, AdditionalData> {
  const tableState = ref<ITableState>({
    page: 1,
  });
  const filterSetId = computed(() => tableState.value?.filter_set_id);

  const {
    data,
    isPlaceholderData: isLoading,
    isFetching,
    setData,
    refetch,
  } = useQuery(tableState, ...args);

  function updateTableState(state: Partial<ITableState>, action?: ITableStateUpdateAction): void {
    tableState.value = {
      ...state,
      page: state.page || 1,
    };

    if (action === 'applyFilter' && !isFetching.value) {
      void refetch({ cancelRefetch: false });
    }
  }

  async function refreshTableData() {
    return await refetch({ cancelRefetch: false });
  }

  return {
    tableState,
    filterSetId,
    data,
    isLoading,
    isFetching,
    updateTableState,
    setData,
    refetch,
    refreshTableData,
  };
}
